import { styled } from '@mui/material/styles'
import React from 'react'
import { required } from 'react-admin'
import { Food } from 'services/api/entities/catalog/food/types/internal/food-models'
import { Logistic } from 'services/api/entities/catalog/logistic/types/internal/logistic-models'
import { APILogisticSnapshot } from 'services/api/entities/events/types/api/response-models'
import { FoodSelection } from 'services/api/entities/events/types/internal/selections'
import { objArrayIntersection, getId } from 'services/utils'
import EventUpsellingSelectionInput from 'ui-shared/FormComponents/EventUpsellingSelectionInput'
import { SelectionDetailsProps } from 'ui-shared/FormComponents/MainProductSelectionInputContent'
import LocalizedSelectInput from 'ui-shared/LocalizedFields/LocalizedSelectInput'
import TimePicker from 'ui-shared/WrappedInputs/TimePicker'
import IntolerancesInput from '../../EventsComponents/IntolerancesInput'
import OptionSelectionInput from '../../EventsComponents/OptionSelectionInput'
import { getSelectionChoices } from '../../utils'

const PREFIX = 'FoodSelectionDetailsForm'

const classes = {
  dFlex: `${PREFIX}-dFlex`,
  mR: `${PREFIX}-mR`,
  displayVertically: `${PREFIX}-displayVertically`,
  defaultWidth: `${PREFIX}-defaultWidth`,
  arrayInputPadding: `${PREFIX}-arrayInputPadding`,
}

// TODO jss-to-styled codemod: The Fragment root was replaced by div. Change the tag if needed.
const Root = styled('div')({
  [`& .${classes.dFlex}`]: {
    display: 'flex',
    width: '100%',
  },
  [`& .${classes.mR}`]: {
    marginRight: '8px',
  },
  [`& .${classes.displayVertically}`]: {
    display: 'flex',
    flexDirection: 'column',
    width: '256px',
  },
  [`& .${classes.defaultWidth}`]: {
    width: '256px',
  },
  [`& .${classes.arrayInputPadding}`]: {
    padding: '16px 0 0 0',
  },
})

const FoodSelectionDetailsForm: React.FC<
  SelectionDetailsProps<Food, FoodSelection>
> = ({ scopedFormData, selectedProduct, getSource, isObsolete, formData }) => {
  if (!scopedFormData || !formData) return null

  const { upsellingSelection = [], logisticSelection } = scopedFormData

  const formSelectedUpsellingsFull =
    selectedProduct?.upsellingItemsFull.filter((ups) =>
      upsellingSelection.map((u) => u?.id).includes(ups.id)
    ) ?? []

  const availableLogistics = upsellingSelection.length
    ? (objArrayIntersection(
        selectedProduct?.logisticsFull ?? [],
        ...formSelectedUpsellingsFull.map((ups) => ups.logisticsFull)
      ) as Logistic[])
    : selectedProduct?.logisticsFull ?? []

  const logisticChoices = getSelectionChoices(
    logisticSelection,
    availableLogistics
  )

  if (
    !!logisticSelection?.id &&
    !availableLogistics.map(getId).includes(logisticSelection.id)
  ) {
    // the first logistic is mandatory for each foodBundle and upselling and is the default one in the case
    // selecting an upselling reduces the overall selectable logistic options
    scopedFormData.logisticSelection =
      logisticChoices.find((e) => e.id === logisticSelection.id) ??
      selectedProduct?.logisticsFull[0]
  }

  return (
    <Root>
      <TimePicker
        source={getSource!('deliveryTime')}
        label="form.fields.deliveryTime"
        validate={required()}
        disabled={isObsolete}
      />

      <IntolerancesInput
        disabled={isObsolete}
        source={getSource!('intolerances')}
      />

      {/* if the product is obsolete and there is no selection we do not show the empty disabled component */}
      {(!isObsolete || !!upsellingSelection?.length) && (
        <EventUpsellingSelectionInput
          selectedProduct={selectedProduct}
          source={getSource!('upsellingSelection')}
          upsellingSelection={upsellingSelection}
          disableInputs={isObsolete}
        />
      )}

      <LocalizedSelectInput
        source={getSource!('logisticSelection')}
        label="Catering Logistic"
        format={(v: APILogisticSnapshot) => v?.id ?? ''}
        parse={(v: number) => logisticChoices.find((l) => l.id === v)}
        choices={logisticChoices}
        forceDisabled={isObsolete}
        fullWidth
      />

      {!!selectedProduct?.options?.length && (
        <OptionSelectionInput
          selectedProduct={selectedProduct}
          eventOptioned={scopedFormData}
          source={getSource!('optionSelections')}
          isObsolete={isObsolete}
        />
      )}
    </Root>
  )
}

export default FoodSelectionDetailsForm
